// React Imports
import { ReactElement } from "react";

// Third Party Libraries Imports
import moment from "moment";
import ReactCountryFlag from "react-country-flag";

// Local Imports
import CollapseComponent from "../../../../components/collapse.component";
import clsx from "clsx";
import { Divider } from "antd";

interface AccountDetailsProps {
  [key: string]: any;
}

const AccountDetails: React.FC<AccountDetailsProps> = ({
  accountKycDetails,
}: AccountDetailsProps): ReactElement => {
  return (
    <section className="flex flex-col w-full h-full ">
      <div className="flex flex-col p-[10px]">
        <CollapseComponent
          header={
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-xl font-semibold">Account details</h3>
            </div>
          }
        >
          {accountKycDetails?.clientSubType?.description === "Minor" && (
            <h3 className=" text-lg font-semibold mb-3">Minor details</h3>
          )}
          <div className="flex gap-5 flex-wrap mb-5">
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Account code
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountCode || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Account type
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.clientSubType?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Account status
              </p>
              {accountKycDetails?.accountStatus?.description ? (
                <span
                  className={clsx(
                    " text-base font-semibold text-white w-fit px-3 py-2 rounded-md",
                    accountKycDetails?.accountStatus?.description === "Active"
                      ? "bg-green-100"
                      : accountKycDetails?.accountStatus?.description ===
                        "Pending"
                      ? "bg-yellow-500"
                      : "bg-red-100"
                  )}
                >
                  {accountKycDetails?.accountStatus?.description || "-"}
                </span>
              ) : (
                "-"
              )}
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Title
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.title?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                English name
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.nameEn || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Arabic name
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.nameAr || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Gender
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.gender?.description || "-"}
              </span>
            </div>
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  National ID
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.civilIdNumber || "-"}
                </span>
              </div>
            )}
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  National ID expiry date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.civilIdExpiry
                    ? moment(
                        accountKycDetails?.accountInfo[0]?.civilIdExpiry
                      ).format("MMM DD, YYYY")
                    : "-"}
                </span>
              </div>
            )}
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Passport number
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.passportNumber || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Passport expiry date
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.passportNumber
                  ? moment(
                      accountKycDetails?.accountInfo[0]?.passportExpiry
                    )?.format("MMM DD, YYYY")
                  : "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Passport issuing country
              </p>
              <div className="flex items-center gap-2">
                {accountKycDetails?.accountInfo[0]?.passportIssuePlace
                  ?.nameEn ? (
                  <>
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        accountKycDetails?.accountInfo[0]?.passportIssuePlace
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {
                        accountKycDetails?.accountInfo[0]?.passportIssuePlace
                          ?.nameEn
                      }
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Passport issue date
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.passportNumber
                  ? moment(
                      accountKycDetails?.accountInfo[0]?.passportIssueDate
                    )?.format("MMM DD, YYYY")
                  : "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Residence
              </p>
              <div className="flex items-center gap-2">
                {accountKycDetails?.accountInfo[0]?.residentialCountry
                  ?.nameEn ? (
                  <>
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        accountKycDetails?.accountInfo[0]?.residentialCountry
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {
                        accountKycDetails?.accountInfo[0]?.residentialCountry
                          ?.nameEn
                      }
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Birth place - Country
              </p>
              <div className="flex items-center gap-2">
                {accountKycDetails?.accountInfo[0]?.birthPlace?.nameEn ? (
                  <>
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        accountKycDetails?.accountInfo[0]?.birthPlace
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {accountKycDetails?.accountInfo[0]?.birthPlace?.nameEn}
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Birth place - City
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.birthPlaceCity || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Birth date
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {moment(accountKycDetails?.accountInfo[0]?.birthDate).format(
                  "MMM DD, YYYY"
                ) || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Nationality
              </p>
              <div className="flex items-center gap-2">
                {accountKycDetails?.accountInfo[0]?.nationality
                  ?.nationalityEn ? (
                  <>
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        accountKycDetails?.accountInfo[0]?.nationality
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {
                        accountKycDetails?.accountInfo[0]?.nationality
                          ?.nationalityEn
                      }
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Other nationality
              </p>
              <div className="flex items-center gap-2">
                {accountKycDetails?.accountInfo[0]?.otherNationality
                  ?.nationalityEn ? (
                  <>
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        accountKycDetails?.accountInfo[0]?.otherNationality
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {
                        accountKycDetails?.accountInfo[0]?.otherNationality
                          ?.nationalityEn
                      }
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Number of dependants
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.numberDependents || "-"}
                </span>
              </div>
            )}
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Marital status
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.maritalStatus
                    ?.description || "-"}
                </span>
              </div>
            )}
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  ID type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.idType?.description ||
                    "-"}
                </span>
              </div>
            )}
          </div>
          {accountKycDetails?.clientSubType?.description === "Minor" && (
            <Divider />
          )}
          {accountKycDetails?.clientSubType?.description === "Minor" && (
            <h3 className=" text-lg font-semibold mb-3">Guardian details</h3>
          )}
          {accountKycDetails?.clientSubType?.description === "Minor" && (
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  Guardian
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Title
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0].guardianTitle
                    ?.description || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  English name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0].guardianNameEn || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Arabic name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0].guardianNameAr || "-"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Residence
                </p>
                <div className="flex items-center gap-2">
                  <ReactCountryFlag
                    style={{
                      fontSize: "24px",
                    }}
                    countryCode={
                      accountKycDetails?.accountInfo[0]?.residentialCountry
                        ?.countryCode
                    }
                    svg
                  />
                  <span className=" text-base font-semibold text-lightGray-200">
                    {accountKycDetails?.accountInfo[0].residentialCountry
                      ?.nameEn || "-"}
                  </span>
                </div>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Gender
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0].guardianGender
                    ?.description || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Birth date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {moment(
                    accountKycDetails?.accountInfo[0].guardianBirthDate
                  ).format("MMM DD, YYYY") || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Nationality
                </p>
                <div className="flex gap-2 items-center">
                  {accountKycDetails?.accountInfo[0].guardianCitizenship
                    ?.nationalityEn ? (
                    <>
                      <ReactCountryFlag
                        style={{
                          fontSize: "24px",
                        }}
                        countryCode={
                          accountKycDetails?.accountInfo[0].guardianCitizenship
                            ?.countryCode
                        }
                        svg
                      />
                      <span className=" text-base font-semibold text-lightGray-200">
                        {
                          accountKycDetails?.accountInfo[0].guardianCitizenship
                            ?.nationalityEn
                        }
                      </span>
                    </>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>
          )}
        </CollapseComponent>
        <CollapseComponent
          header={
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-xl font-semibold">
                Wealth management team details
              </h3>
            </div>
          }
        >
          {accountKycDetails?.wealthManager?.id ? (
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager 1
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.ae1 || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager email 1
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.ae1Email || "-"}
                </span>
              </div>
              {accountKycDetails?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager 2
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {accountKycDetails?.wealthManager?.ae2 || "-"}
                  </span>
                </div>
              )}
              {accountKycDetails?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager email 2
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {accountKycDetails?.wealthManager?.ae2Email || "-"}
                  </span>
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Team lead
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.teamLead || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Team lead email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.teamLeadEmail || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Team
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.team || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Team email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.wealthManager?.teamEmail || "-"}
                </span>
              </div>
            </div>
          ) : (
            <div className=" flex items-center justify-center flex-col">
              <img src="/images/no-data.svg" alt="no data" />
              <p className=" text-lightGray-200 my-3 text-base font-medium">
                No wealth management team details
              </p>
            </div>
          )}
        </CollapseComponent>
        <CollapseComponent
          header={
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-xl font-semibold">Contact person details</h3>
            </div>
          }
        >
          {accountKycDetails?.contactPersons?.length ? (
            accountKycDetails?.contactPersons?.map(
              (person: any, index: number) => (
                <div className="flex gap-5 flex-wrap mb-5" key={index}>
                  <h4 className=" font-semibold w-full">Person #{index + 1}</h4>
                  <div className="w-[32%] flex flex-col">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Title
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.prefix?.description || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Full name
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.fullName || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Email
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.email || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Relationship
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.relationship?.description || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Mobile number
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.mobileNumber || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Address details
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {person?.details || "-"}
                    </span>
                  </div>
                  {index + 1 !== accountKycDetails?.contactPersons?.length && (
                    <Divider />
                  )}
                </div>
              )
            )
          ) : (
            <div className=" flex items-center justify-center flex-col">
              <img src="/images/no-data.svg" />
              <p className=" text-lightGray-200 my-3 text-base font-medium">
                No contact person details
              </p>
            </div>
          )}
        </CollapseComponent>
        {accountKycDetails?.paciData?.length ? (
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">PACI details</h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Civil ID number
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.civilId || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  English name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.fullNameEn || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Arabic name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.fullNameAr || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Gender
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.gender === "F"
                    ? "Female"
                    : "Male"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Birth date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.birthDate
                    ? moment(accountKycDetails?.paciData[0]?.birthDate).format(
                        "MMM DD, YYYY"
                      )
                    : "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Expiry date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.paciData[0]?.cardExpirationDate
                    ? moment(
                        accountKycDetails?.paciData[0]?.cardExpirationDate
                      ).format("MMM DD, YYYY")
                    : "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Nationality
                </p>
                <div className="flex items-center gap-2">
                  {accountKycDetails?.paciData[0]?.nationalityAr ? (
                    <span className=" text-base font-semibold text-lightGray-200">
                      {accountKycDetails?.paciData[0]?.nationalityAr}
                    </span>
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>
          </CollapseComponent>
        ) : (
          ""
        )}
      </div>
    </section>
  );
};

export default AccountDetails;
